import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { getProfessional, IProfessionalProfile } from "../../api/getProfessional";
import ProfessionalProfileLoader from "../loaders/ProfessionalProfileLoader";
import OfferedProcedures from "./OfferedProcedures";
import ProfessionalSubEspecialty from "./ProfessionalSubEspecialty";
import StarIndicator from "./StarIndicator";

const ProfessionalInfosWrapper = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: center;
	align-content: stretch;
	width: 100%;
`;

const ProfessionalProfileWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: center;
	align-content: stretch;
	padding: 28px 18px 16px 18px;
`;

const ProfessionalProfileAvatar = styled.img`
	width: 105px;
	height: 105px;
	border-radius: 105px;
`;

const ProfessionalInfoWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	justify-content: flex-end;
	align-items: stretch;
	align-content: stretch;
	height: fit-content;
	margin-left: 12px;
`;

const ButtonMaisSobre = styled.div`
	color: ${props => (props.theme.colors.blackVitat || "#002b49")};
	background: ${props => (props.theme.colors.whiteVitat || "#eadced")};
	box-shadow: 0px 4px 39px 6px rgba(0, 0, 0, 0.07);
	border-radius: 5px;
	font-family: Livvic;
	font-style: normal;
	font-weight: 600;
	font-size: 12.5px;
	line-height: 165.5%;
	padding-left: 10px;
	padding-right: 10px;
	margin-left: -6px;
`;

const ProfessionalProfileNome = styled.p`
	margin: 0px;
	font-family: Livvic;
	font-style: normal;
	font-weight: 500;
	font-size: 17px;
	line-height: 165.5%;
	color: ${props => (props.theme.colors.blackVitat || "#002b49")};
	text-align: left;
`;

const ProfessionalProfileEspecialidade = styled.p`
	margin: 0px;
	font-family: Livvic;
	font-style: normal;
	font-weight: normal;
	font-size: 14px;
	line-height: 165.5%;
	color: ${props => (props.theme.colors.blackVitat || "#002b49")};
	text-align: left;
`;

const ProfessionalScheduleInfo = styled.div`
	width: 100%;
	padding: 0 6px;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 12px;
`;

interface ProfessionalProfileProps {
	codProfessional?: string;
	professionalData?: IProfessionalProfile;
	canSeeReviews?: boolean;
	showMoreAboutButton?: boolean;
	showOfferedProcedures?: boolean;
}

const ProfessionalProfile: FC<ProfessionalProfileProps> = ({
	codProfessional,
	professionalData,
	canSeeReviews = true,
	showMoreAboutButton = true,
	showOfferedProcedures = false,
}) => {
	const [professional, setProfessional] = useState<IProfessionalProfile | undefined>(professionalData);
	const navigate = useNavigate();

	const carregaDados = async (codProfessional: string) => {
		if (professional) return;

		setProfessional(undefined);
		const professionalResponse: IProfessionalProfile = await getProfessional(codProfessional);
		setProfessional(professionalResponse);
	};

	const handleReviews = () => {
		if (!canSeeReviews) return;

		navigate(`/medicos/${codProfessional}/reviews`);
	};

	useEffect(() => {
		if (codProfessional) carregaDados(codProfessional);
		return () => {
			setProfessional(undefined);
		};
	}, []);

	useEffect(() => {
		setProfessional(professionalData);
	}, [professionalData]);

	if (professional) {
		return (
			<ProfessionalProfileWrapper>
				<ProfessionalInfosWrapper>
					<ProfessionalProfileAvatar src={professional.imagem || ""} />
					<ProfessionalInfoWrapper>
						<ProfessionalProfileNome>{professional.nome || ""}</ProfessionalProfileNome>
						<ProfessionalProfileEspecialidade>
							{professional.especialidade || ""} {professional.registro && " - " + professional.registro + " " + professional.numeroRegistro}
						</ProfessionalProfileEspecialidade>
						<StarIndicator
							grade={professional.avaliacao}
							maxGrade={5}
							reviewsQuantidade={professional.contagemAvaliacao}
							handleTotalReviewsClick={handleReviews}
						/>
						{showMoreAboutButton && (
							<ButtonMaisSobre
								onClick={() => {
									navigate(`/medicos/${codProfessional}`);
								}}
							>
								Mais sobre este profissional
							</ButtonMaisSobre>
						)}
					</ProfessionalInfoWrapper>
				</ProfessionalInfosWrapper>
				<ProfessionalScheduleInfo>
					{professional.subespecialidade && professional.subespecialidade.length > 0 && (
						<ProfessionalSubEspecialty>{`Subespecialidades: ${professional.subespecialidade.join(", ")}`}</ProfessionalSubEspecialty>
					)}
					{/* {showOfferedProcedures && professional.procedimentos.length > 0 && (
						<OfferedProcedures>
							<b>{professional.procedimentos.length > 1 ? "Procedimentos oferecidos: " : "Procedimento oferecido: "}</b>
							{professional.procedimentos.map(procedimento => procedimento.description).join(", ")}
						</OfferedProcedures>
					)} */}
				</ProfessionalScheduleInfo>
			</ProfessionalProfileWrapper>
		);
	} else {
		return <ProfessionalProfileLoader />;
	}
};

export default ProfessionalProfile;
